import * as Yup from "yup";
import type { AdditionalQuestionsConfig, SchemaShape } from "../../types";
import { validateAdditionalQuestions } from "./additionalQuestionSchema";
import { validateDocumentsUpload } from "./validateDocumentsUpload";
import { OrganizerPages } from "@constants/TaxOrganizer.ts";

export const formSchema = Yup.object().shape({
  method1040: Yup.array().of(
    Yup.object().shape({
      method: Yup.string().required("Method is required"),
    })
  ),
  personalInfo: Yup.array().of(
    Yup.object().shape({
      filingStatus: Yup.string().required("Filing status is required"),
      incomeType: Yup.array()
        .of(Yup.string())
        .min(1, "At least one income type must be selected")
        .required("Income Type is required"),
      spouseIncomeType: Yup.lazy((_, { parent }) => {
        const needSpouseIncomeTypes = [
          "Single",
          "Head of Household",
          "Married Filing Separately",
        ];
        if (!needSpouseIncomeTypes.includes(parent.filingStatus)) {
          return Yup.array()
            .of(Yup.string())
            .min(1, "At least one income type must be selected");
        }
        return Yup.array().of(Yup.string());
      }),
      numContractingJobs: Yup.number()
        .transform((value, originalValue) => {
          return originalValue === "" || originalValue === null ? 0 : value;
        })
        .default(1)
        .moreThan(0, "Number of contracting jobs must be greater than zero"),
      spouseNumContractingJobs: Yup.number()
        .transform((value, originalValue) => {
          return originalValue === "" || originalValue === null ? 0 : value;
        })
        .default(1)
        .moreThan(0, "Number of contracting jobs must be greater than zero"),
      spouseOtherIncome: Yup.string().when("spouseIncomeType", {
        is: (spouseIncomeType: string[]) => spouseIncomeType?.includes("Other"),
        then: (schema) => schema.required("Please describe the other income"),
        otherwise: (schema) => schema.nullable(),
      }),
      numDependents: Yup.number()
        .nullable()
        .transform((value, originalValue) =>
          String(originalValue).trim() === "" ? null : value
        )
        .max(30, "Number of dependents must be less than 30"),
      numJobs: Yup.number()
        .transform((value, originalValue) => {
          return originalValue === "" || originalValue === null
            ? undefined
            : value;
        })
        .when("incomeType", {
          is: (incomeType: string[]) => incomeType?.includes("employee"),
          then: (schema) =>
            schema
              .required("Please enter the number of jobs")
              .moreThan(0, "Number of jobs must be greater than zero"),
          otherwise: (schema) => schema.nullable(),
        }),
      spouseNumJobs: Yup.number()
        .transform((value, originalValue) => {
          return originalValue === "" || originalValue === null
            ? undefined
            : value;
        })
        .when("spouseIncomeType", {
          is: (spouseIncomeType: string[]) =>
            spouseIncomeType?.includes("employee"),
          then: (schema) =>
            schema
              .required("Please enter the number of jobs")
              .moreThan(0, "Number of jobs must be greater than zero"),
          otherwise: (schema) => schema.nullable(),
        }),
      numBusinesses: Yup.number()
        .transform((value, originalValue) => {
          return originalValue === "" || originalValue === null ? 0 : value;
        })
        .default(1)
        .moreThan(0, "Number of businesses must be greater than zero")
        .max(20, "Number of businesses must be less than 20"),
      otherIncome: Yup.string().when("incomeType", {
        is: (incomeType: string[]) => incomeType?.includes("Other"),
        then: (schema) => schema.required("Please describe the other income"),
        otherwise: (schema) => schema.nullable(),
      }),
    })
  ),
  docInfo: Yup.array().of(
    Yup.object().shape({
      tiles: Yup.array()
        .of(Yup.number())
        .min(1, "Please select at least one tile or select not applicable")
        .required("At least one tile is required or select not applicable"),
    })
  ),

  identityVerification: Yup.array().of(
    Yup.object().shape({
      firstName: Yup.string().required("First name is required"),
      middleName: Yup.string(),
      lastName: Yup.string().required("Last name is required"),
      dateOfBirth: Yup.string()
        .test("valid-date", "Invalid date format", (value) => {
          if (!value) return false;

          // Extract date portion if ISO format (handles backend returning ISO dates)
          const dateStr = value.includes("T") ? value.split("T")[0] : value;

          // Validate YYYY-MM-DD format
          if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return false;

          // Validate it's a real date
          const [year, month, day] = dateStr.split("-").map(Number);
          const date = new Date(year, month - 1, day);
          return (
            date.getFullYear() === year &&
            date.getMonth() === month - 1 &&
            date.getDate() === day
          );
        })
        .required("Date of birth is required"),
      address: Yup.string().required("Address is required"),
      addressLine2: Yup.string(),
      city: Yup.string().required("City is required"),
      ipPin: Yup.string()
        .test("ipPin", "IP PIN must be exactly 6 digits if provided", (val) => {
          if (!val || val.trim() === "") return true; // Empty or whitespace-only strings are allowed
          return /^\d{6}$/.test(val); // Must be exactly 6 digits if not empty
        })
        .nullable(),
      occupation: Yup.string().required("Occupation is required"),
      SSN: Yup.string()
        .required("SSN is required")
        .matches(/^\d{3}-\d{2}-\d{4}$/, "SSN must be 9 digits")
        .oneOf([Yup.ref("confirmSSN")], "SSN confirmation must match SSN"),
      confirmSSN: Yup.string().when("SSN", (SSN, schema) => {
        return SSN
          ? schema
              .required("SSN confirmation is required")
              .oneOf([Yup.ref("SSN")], "SSN confirmation must match SSN")
          : schema.notRequired();
      }),
      state: Yup.string().required("State is required"),
      country: Yup.string()
        .required("Country is required")
        .test(
          "country-validation",
          "Country must be United States when state is selected",
          function (value) {
            const { state } = this.parent;
            if (
              state !== "Other State or Province" &&
              value !== "United States"
            ) {
              return false;
            }
            return true;
          }
        ),
      yourPhone: Yup.string()
        .required("Phone number is required")
        .matches(
          /^\(\d{3}\) \d{3}-\d{4}$/,
          "Phone number must be in format (XXX) XXX-XXXX"
        ),
      zip: Yup.string()
        .required("ZIP code is required")
        .matches(/^\d{5}(-\d{4})?$/, "Invalid ZIP code format"),
    })
  ),

  comments: Yup.object().shape({
    additonalComments: Yup.string(),
  }),

  businessInfo: Yup.array().of(
    Yup.object().shape({
      businessName: Yup.string().required("Business name is required"),
      employerIdentification: Yup.string().matches(
        /^\d{9}$/,
        "EIN must be exactly 9 digits"
      ),
      industry: Yup.string().required("Industry is required"),
      businessAddress: Yup.string().required("Business address is required"),
      businessAddressLine2: Yup.string(),
      businessCity: Yup.string().required("Business city is required"),
      businessCountry: Yup.string().required("Business country is required"),
      businessState: Yup.string().required("Business state is required"),
      businessZip: Yup.string()
        .required("Business ZIP is required")
        .matches(/^\d{5}(-\d{4})?$/, "Invalid ZIP code format"),
      expenses: Yup.array().of(
        Yup.object()
          .shape({
            category: Yup.string()
              .trim()
              .transform((value) => (value === "" ? undefined : value)),
            amount: Yup.number()
              .typeError("Please enter an amount as a number")
              .transform((value) => (isNaN(value) ? undefined : value)),
          })
          .test("category-amount-consistency", function (value) {
            const { category, amount } = value || {};
            const hasCategory = !!category?.trim();
            const hasAmount = amount !== undefined;

            // Allow both to be empty
            if (!hasCategory && !hasAmount) return true;

            // Both provided = valid
            if (hasCategory && hasAmount) return true;

            // Get current path (e.g., "expenses[0]")
            const currentPath = this.path;

            // Determine error path (e.g., "expenses[0].amount")
            const errorPath = `${currentPath}.${
              hasCategory ? "amount" : "category"
            }`;

            return this.createError({
              path: errorPath, // Targets the specific field in the array
              message: hasCategory
                ? "Amount is required if category is entered"
                : "Category is required if amount is entered",
            });
          })
      ),
      revenue: Yup.number()
        .transform((value, originalValue) => {
          return originalValue === "" || originalValue === null ? 0 : value;
        })
        .required("Revenue is required")
        .moreThan(0, "Business revenue must be greater than 0"),
    })
  ),

  credits: Yup.array().of(
    Yup.object().shape({
      charity: Yup.array().of(
        Yup.object().shape({
          "charity name": Yup.string().required("Please enter a charity name"),
          amount: Yup.number()
            .typeError("Please enter an amount as a number")
            .required("Please enter an amount"),
          //TODO: add validation for cents
        })
      ),
    })
  ),

  daycareInfo: Yup.array().of(
    Yup.object().shape({
      dayCareSelect: Yup.string().required(),
      dayCareEIN: Yup.string().when(
        "dayCareSelect",
        (dayCareSelect: string[], schema: Yup.StringSchema) => {
          return dayCareSelect[0] === "My children attend the same daycare"
            ? schema
                .required(
                  "Please enter the EIN / SSN or the daycare / childcare provider"
                )
                .matches(/^\d{9}$/, "EIN must be exactly 9 digits")
            : schema;
        }
      ),
      daycareName: Yup.string().when(
        "dayCareSelect",
        (dayCareSelect: string[], schema: Yup.StringSchema) => {
          return dayCareSelect[0] === "My children attend the same daycare"
            ? schema.required("Please enter the name of the daycare")
            : schema;
        }
      ),
      daycareTuition: Yup.number().when(
        "dayCareSelect",
        (
          dayCareSelect: string[],
          schema: Yup.NumberSchema | Yup.StringSchema
        ) => {
          return dayCareSelect[0] === "My children attend the same daycare"
            ? schema.required(
                "Please enter the amount you paid in tuition for the entire year last year"
              )
            : schema;
        }
      ),
      differentDaycares: Yup.array().when(
        "dayCareSelect",
        (
          dayCareSelect: string[],
          schema: Yup.ArraySchema<
            any[] | undefined,
            Yup.AnyObject,
            undefined,
            ""
          >
        ) => {
          return dayCareSelect[0] === "My children attend different daycares"
            ? schema
                .of(
                  Yup.object().shape({
                    "Name of Daycare / Childcare Provider":
                      Yup.string().required(
                        "Please enter the name of the daycare"
                      ),
                    "EIN or SSN for Daycare / Childcare Provider": Yup.string()
                      .required(
                        "Please enter the EIN / SSN or the daycare / childcare provider"
                      )
                      .matches(/^\d{9}$/, "EIN must be exactly 9 digits"),
                    "How much did you pay in tuition for the entire year last year?":
                      Yup.number().required(
                        "Please enter the amount you paid in tuition for the entire year last year"
                      ),
                  })
                )
                .required()
            : schema;
        }
      ),
    })
  ),

  dependentsInfo: Yup.array().of(
    Yup.object().shape({
      firstName: Yup.string().required("First name is required"),
      middleName: Yup.string(),
      lastName: Yup.string().required("Last name is required"),
      SSN: Yup.string()
        .matches(/^\d{3}-\d{2}-\d{4}$/, "SSN must be 9 digits")
        .required("SSN is required"),
      confirmSSN: Yup.string()
        .oneOf([Yup.ref("SSN")], "SSN confirmation must match SSN")
        .required("SSN confirmation is required"),
      dateOfBirth: Yup.string()
        .test("valid-date", "Invalid date format", (value) => {
          if (!value) return false;

          // Extract date portion if ISO format (handles backend returning ISO dates)
          const dateStr = value.includes("T") ? value.split("T")[0] : value;

          // Validate YYYY-MM-DD format
          if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return false;

          // Validate it's a real date
          const [year, month, day] = dateStr.split("-").map(Number);
          const date = new Date(year, month - 1, day);
          return (
            date.getFullYear() === year &&
            date.getMonth() === month - 1 &&
            date.getDate() === day
          );
        })
        .required("Date of birth is required"),
      dependentsDaycareChoice: Yup.string().required("Please select an option"),
      dependentsMedicalChoice: Yup.string().required("Please select an option"),
      dependentsSelect: Yup.string().required("Please select an option"),
      relationship: Yup.string().trim().required("Relationship is required"),
    })
  ),

  disclosureAgreement: Yup.object().shape({
    disclosureAccepted: Yup.boolean().oneOf(
      [true],
      "You must accept the disclosure agreement"
    ),
  }),

  formerAddress: Yup.array().of(
    Yup.object().shape({
      address: Yup.array().of(
        Yup.object().shape({
          "state / province": Yup.string().required(
            "State/Province is required"
          ),
          "city / town": Yup.string().required("City/Town is required"),
        })
      ),
    })
  ),

  medicalExpenses: Yup.array().of(
    Yup.object().shape({
      didNotContribute: Yup.boolean(),
      medical: Yup.array().of(
        Yup.object().shape({
          provider: Yup.string().required("Please enter a medical provider"),
          amount: Yup.number()
            .typeError("Please enter an amount as a number")
            .required("Please enter an amount"),
        })
      ),
    })
  ),

  refundMethod: Yup.array().of(
    Yup.object().shape({
      method: Yup.string().required("Refund method is required"),
      accountType: Yup.string().when("method", {
        is: (val: string) => val === "Deposit my refund into my bank account",
        then: () => Yup.string().required("Account type is required"),
      }),
      accountNumber: Yup.string().when("method", {
        is: (val: string) => val === "Deposit my refund into my bank account",
        then: () =>
          Yup.string()
            .required("Account number is required")
            .min(4, "Account number must be at least 4 characters")
            .max(17, "Account number must not exceed 17 characters"),
      }),
      confirmAccountNumber: Yup.string().when("method", {
        is: (val: string) => val === "Deposit my refund into my bank account",
        then: () =>
          Yup.string()
            .required("Account number confirmation is required")
            .oneOf(
              [Yup.ref("accountNumber")],
              "Account number confirmation must match account number"
            ),
      }),
      mailingAddress: Yup.string().when("method", {
        is: (val: string) =>
          val === "Send my refund check as a check in the mail",
        then: () => Yup.string().required("Address is required"),
        otherwise: () => Yup.string(),
      }),
      mailingAddressLine2: Yup.string(),
      mailingCity: Yup.string().when("method", {
        is: (val: string) =>
          val === "Send my refund check as a check in the mail",
        then: () => Yup.string().required("City is required"),
        otherwise: () => Yup.string(),
      }),
      mailingCountry: Yup.string().when("method", {
        is: (val: string) =>
          val === "Send my refund check as a check in the mail",
        then: () => Yup.string().required("Country is required"),
        otherwise: () => Yup.string().nullable(),
      }),
      mailingState: Yup.string().when("method", {
        is: (val: string) =>
          val === "Send my refund check as a check in the mail",
        then: () => Yup.string().required("State is required"),
        otherwise: () => Yup.string(),
      }),
      mailingZip: Yup.string().when("method", {
        is: (val: string) =>
          val === "Send my refund check as a check in the mail",
        then: () =>
          Yup.string()
            .required("ZIP is required")
            .matches(/^\d{5}(-\d{4})?$/, "Invalid ZIP code format"),
        otherwise: () => Yup.string(),
      }),
      routingNumber: Yup.string().when("method", {
        is: (val: string) => val === "Deposit my refund into my bank account",
        then: () =>
          Yup.string()
            .required("Routing number is required")
            .matches(/^\d{9}$/, "Routing number must be 9 digits"),
      }),
    })
  ),

  spouseInfo: Yup.array().of(
    Yup.object().shape({
      spouseFirstName: Yup.string().required("Spouse first name is required"),
      spouseMiddleName: Yup.string(),
      spouseLastName: Yup.string().required("Spouse last name is required"),
      spouseEmail: Yup.string()
        .required("Spouse email is required")
        .email("Please enter a valid email address"),
      spouseConfirmSSN: Yup.string()
        .required("Spouse SSN confirmation is required")
        .oneOf([Yup.ref("spouseSSN")], "SSN confirmation must match SSN"),
      spouseDateOfBirth: Yup.string()
        .test("valid-date", "Invalid date format", (value) => {
          if (!value) return false;

          // Extract date portion if ISO format (handles backend returning ISO dates)
          const dateStr = value.includes("T") ? value.split("T")[0] : value;

          // Validate YYYY-MM-DD format
          if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return false;

          // Validate it's a real date
          const [year, month, day] = dateStr.split("-").map(Number);
          const date = new Date(year, month - 1, day);
          return (
            date.getFullYear() === year &&
            date.getMonth() === month - 1 &&
            date.getDate() === day
          );
        })
        .required("Spouse date of birth is required"),
      spouseIpPin: Yup.string()
        .test(
          "spouseIpPin",
          "IP PIN must be exactly 6 digits if provided",
          (val) => {
            if (!val || val.trim() === "") return true; // Empty or whitespace-only strings are allowed
            return /^\d{6}$/.test(val); // Must be exactly 6 digits if not empty
          }
        )
        .nullable(),
      spouseOccupation: Yup.string().required("Spouse occupation is required"),
      spousePhone: Yup.string()
        .required("Spouse phone number is required")
        .matches(
          /^\(\d{3}\) \d{3}-\d{4}$/,
          "Phone number must be in format (XXX) XXX-XXXX"
        ),
      spouseSSN: Yup.string()
        .required("Spouse SSN is required")
        .matches(/^\d{3}-\d{2}-\d{4}$/, "SSN must be 9 digits"),
    })
  ),
});

/**
 * Validates a field in a form schema.
 *
 * @param {keyof SchemaShape} pageName - The name of the page in the form schema.
 * @param {number} index - The index of the field in the page.
 * @param {string} fieldName - The name of the field to validate.
 * @param {any} value - The value of the field to validate.
 * @param {any} allValues - The values of all fields in the form.
 * @return {Promise<string | null>} - Returns null if there are no errors, otherwise returns the error message.
 */
export const validateField = async (
  pageName: keyof SchemaShape,
  index: number,
  fieldName: string,
  value: any,
  allValues: any
) => {
  try {
    if (fieldName === "SSN" || fieldName === "confirmSSN") {
      const ssnSchema = Yup.object().shape({
        SSN: Yup.string()
          .required("SSN is required")
          .matches(/^\d{3}-\d{2}-\d{4}$/, "SSN must be 9 digits"),
        confirmSSN: Yup.string()
          .required("SSN confirmation is required")
          .oneOf([Yup.ref("SSN")], "SSN confirmation must match SSN"),
      });

      await ssnSchema.validateAt(fieldName, {
        SSN: fieldName === "SSN" ? value : allValues.SSN,
        confirmSSN: fieldName === "confirmSSN" ? value : allValues.confirmSSN,
      });
    } else {
      const tempObj = {
        [String(pageName)]: Array(index + 1)
          .fill({})
          .map((_, i) => (i === index ? { ...allValues } : {})),
      };

      const fieldPath = fieldName.split(".");
      let currentObj = tempObj[String(pageName)][index];

      for (let i = 0; i < fieldPath.length - 1; i++) {
        if (!currentObj[fieldPath[i]]) {
          currentObj[fieldPath[i]] = {};
        }
        currentObj = currentObj[fieldPath[i]];
      }
      currentObj[fieldPath[fieldPath.length - 1]] = value;

      await formSchema.validateAt(
        `${String(pageName)}[${index}].${fieldName}`,
        tempObj
      );
    }
    return null; // No errors
  } catch (error: any) {
    return error.message; // Return the error message
  }
};

/**
 *
 * @param pageName a page name
 * @param pageData page data
 * @returns a boolean indicating if the page in the organizer is complete or not
 */
export const isPageComplete = (
  pageName: string,
  pageData: any,
  docsToUpload: Record<string, number>,
  responsesDocuments: Record<string, any[]>,
  additionalQuestionsConfig?: AdditionalQuestionsConfig
): boolean => {
  if (!pageData || (Array.isArray(pageData) && pageData.length === 0)) {
    return false;
  }

  try {
    return validatePageData(
      pageName,
      pageData,
      docsToUpload,
      responsesDocuments,
      additionalQuestionsConfig
    );
  } catch (error) {
    handleValidationError(pageName, error);
    return false;
  }
};

const validatePageData = (
  pageName: string,
  pageData: any,
  docsToUpload: Record<string, number>,
  responsesDocuments: Record<string, any[]>,
  additionalQuestionsConfig?: AdditionalQuestionsConfig
): boolean => {
  if (pageName === OrganizerPages.ADDITIONAL_QUESTIONS) {
    return validateAdditionalQuestionsPage(pageData, additionalQuestionsConfig);
  }

  if (pageName === "documents" || pageName === "uploadedDocs") {
    return validateDocumentsPage(docsToUpload, responsesDocuments);
  }

  return validateGenericPage(pageName, pageData);
};

const validateAdditionalQuestionsPage = (
  pageData: any,
  additionalQuestionsConfig?: AdditionalQuestionsConfig
): boolean => {
  if (!additionalQuestionsConfig) {
    console.warn(
      "additionalQuestionsConfig is required for additionalQuestions page"
    );
    return false;
  }
  const errors = validateAdditionalQuestions(
    pageData,
    additionalQuestionsConfig
  );
  return errors === null;
};

const validateDocumentsPage = (
  docsToUpload: Record<string, number>,
  responsesDocuments: Record<string, any[]>
): boolean => {
  const docValidationError = validateDocumentsUpload(
    docsToUpload,
    responsesDocuments
  );
  return docValidationError === "";
};

const validateGenericPage = (pageName: string, pageData: any): boolean => {
  const pageSchema =
    formSchema.fields[pageName as keyof typeof formSchema.fields];
  if (!pageSchema) {
    console.warn(`No schema found for page: ${pageName}`);
    return false;
  }
  const schema = pageSchema as Yup.Schema<any>;
  schema.validateSync(pageData, { abortEarly: false });
  return true;
};

const handleValidationError = (pageName: string, error: any): void => {
  if (error instanceof Yup.ValidationError) {
    console.error(`Page ${pageName} is incomplete:`, error.errors);
  } else {
    console.error(`Error validating page ${pageName}:`, error);
  }
};
